import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Pagination, CircularProgress, Alert,
  Dialog, DialogActions, LinearProgress, Checkbox, Slide, Fade,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import ArrowBackIcon     from "@mui/icons-material/ArrowBack";
import RestoreIcon       from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon     from "@mui/icons-material/Inventory2";
import ArchiveIcon       from "@mui/icons-material/Archive";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import CloseIcon         from "@mui/icons-material/Close";
import SelectAllIcon     from "@mui/icons-material/SelectAll";
import DeselectIcon      from "@mui/icons-material/Deselect";
import api from "../../../services/api";

/* ─────────────────────────────────────────────
   Design Tokens
───────────────────────────────────────────── */
const C = {
  primary:    "#2563EB",
  primaryBg:  "#EFF6FF",
  surface:    "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border:     "#E2E8F0",
  borderHov:  "#CBD5E1",
  text1:      "#0F172A",
  text2:      "#475569",
  text3:      "#94A3B8",
  success:    "#16A34A",
  successBg:  "#F0FDF4",
  successBdr: "#BBF7D0",
  warn:       "#D97706",
  warnBg:     "#FFFBEB",
  warnBdr:    "#FDE68A",
  danger:     "#DC2626",
  dangerBg:   "#FEF2F2",
  dangerBdr:  "#FECACA",
  infoBdr:    "#BFDBFE",
  neutral:    "#64748B",
  neutralBg:  "#F1F5F9",
  neutralBdr: "#CBD5E1",
  amber:      "#B45309",
  amberBg:    "#FFFBEB",
  amberBdr:   "#FDE68A",
};

const CONDITION_MAP = {
  baik:         { label: "Baik",        color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  cukup_baik:   { label: "Cukup Baik",  color: C.primary, bg: C.primaryBg, border: C.infoBdr,    dot: "#3B82F6" },
  rusak_ringan: { label: "Rusak Ringan", color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  rusak_berat:  { label: "Rusak Berat",  color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};
const STATUS_MAP = {
  aktif:           { label: "Aktif",           color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  tidak_aktif:     { label: "Tidak Aktif",     color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" },
  dipinjam:        { label: "Dipinjam",        color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dalam_perbaikan: { label: "Dalam Perbaikan", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

const thSx = {
  fontSize: "0.7rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "12px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = {
  py: "10px", px: "12px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};
const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

function StatusChip({ map, value }) {
  const cfg = map[value] || { label: value || "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px", whiteSpace: "nowrap" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

/* ─────────────────────────────────────────────
   Bulk Action Bar
───────────────────────────────────────────── */
function BulkActionBar({ selectedIds, onClear, onBulkRestore, onBulkDelete, bulkRestoring, bulkDeleting }) {
  const count = selectedIds.length;
  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Box sx={{
        position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
        zIndex: 1300,
        display: "flex", alignItems: "center", gap: 1.5,
        px: 2.5, py: 1.5,
        backgroundColor: C.text1,
        borderRadius: "14px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 420,
        border: `1px solid rgba(255,255,255,0.08)`,
      }}>
        {/* Count badge */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: 1.5, py: 0.5,
          backgroundColor: C.primary, borderRadius: "8px",
          flexShrink: 0,
        }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
            {count} dipilih
          </Typography>
        </Box>

        <Box sx={{ flex: 1, height: 1, backgroundColor: "rgba(255,255,255,0.1)" }} />

        {/* Bulk Restore */}
        <Button
          size="small"
          startIcon={bulkRestoring
            ? <CircularProgress size={12} sx={{ color: "#fff" }} />
            : <RestoreIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkRestore}
          disabled={bulkRestoring || bulkDeleting}
          sx={{
            textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
            color: "#fff", borderRadius: "8px", height: 32, px: 1.5,
            backgroundColor: C.success,
            "&:hover": { backgroundColor: "#15803D" },
            "&:disabled": { backgroundColor: "rgba(22,163,74,0.4)", color: "rgba(255,255,255,0.5)" },
          }}>
          {bulkRestoring ? "Memulihkan..." : "Pulihkan Semua"}
        </Button>

        {/* Bulk Force Delete */}
        <Button
          size="small"
          startIcon={bulkDeleting
            ? <CircularProgress size={12} sx={{ color: "#fff" }} />
            : <DeleteForeverIcon sx={{ fontSize: "14px !important" }} />}
          onClick={onBulkDelete}
          disabled={bulkDeleting || bulkRestoring}
          sx={{
            textTransform: "none", fontSize: "0.78rem", fontWeight: 600,
            color: "#fff", borderRadius: "8px", height: 32, px: 1.5,
            backgroundColor: C.danger,
            "&:hover": { backgroundColor: "#B91C1C" },
            "&:disabled": { backgroundColor: "rgba(220,38,38,0.4)", color: "rgba(255,255,255,0.5)" },
          }}>
          {bulkDeleting ? "Menghapus..." : "Hapus Permanen"}
        </Button>

        {/* Clear selection */}
        <Tooltip title="Batal pilih" arrow>
          <IconButton size="small" onClick={onClear} sx={{
            color: "rgba(255,255,255,0.5)", width: 28, height: 28, borderRadius: "6px",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" },
          }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Slide>
  );
}

/* ═══════════════════════════════════════════
   Main Component
═══════════════════════════════════════════ */
export default function ArchivedItems() {
  const navigate = useNavigate();

  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalItems,  setTotalItems]  = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");

  // Single actions
  const [restoreId,   setRestoreId]   = useState(null);
  const [restoreItem, setRestoreItem] = useState(null);
  const [restoring,   setRestoring]   = useState(false);
  const [deleteId,    setDeleteId]    = useState(null);
  const [deleteItem,  setDeleteItem]  = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  // Bulk actions
  const [selectedIds,        setSelectedIds]        = useState([]);
  const [bulkRestoring,      setBulkRestoring]      = useState(false);
  const [bulkDeleting,       setBulkDeleting]        = useState(false);
  const [bulkRestoreConfirm, setBulkRestoreConfirm] = useState(false);
  const [bulkDeleteConfirm,  setBulkDeleteConfirm]  = useState(false);

  const debounceRef = useRef(null);

  // Derived selection helpers
  const currentPageIds  = items.map(i => i.id);
  const allPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.includes(id));
  const somePageSelected = currentPageIds.some(id => selectedIds.includes(id)) && !allPageSelected;

  // Clear selection on page/search change
  useEffect(() => { setSelectedIds([]); }, [page, search]);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  /* ── Checkbox handlers ── */
  const handleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(id => !currentPageIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };
  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  /* ── Fetch archived items ── */
  const fetchItems = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, per_page: 10 };
      if (search) params.search = search;
      const res = await api.get("/items/archived", { params });
      setItems(res.data.data.data          ?? []);
      setTotalPages(res.data.data.last_page ?? 1);
      setTotalItems(res.data.data.total     ?? 0);
    } catch {
      setError("Gagal memuat data arsip barang.");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  /* ── Single Restore ── */
  const handleRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/items/${restoreId}/restore`);
      setSuccess(`Barang "${restoreItem?.name}" berhasil dipulihkan.`);
      setRestoreId(null); setRestoreItem(null);
      fetchItems();
    } catch {
      setError("Gagal memulihkan barang.");
    } finally { setRestoring(false); }
  };

  /* ── Single Force Delete ── */
  const handleForceDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/items/${deleteId}/force`);
      setSuccess(`Barang "${deleteItem?.name}" berhasil dihapus permanen.`);
      setDeleteId(null); setDeleteItem(null);
      fetchItems();
    } catch {
      setError("Gagal menghapus barang.");
    } finally { setDeleting(false); }
  };

  /* ── Bulk Restore ── */
  const handleBulkRestore = async () => {
    setBulkRestoring(true);
    setBulkRestoreConfirm(false);
    try {
      await api.post("/items/bulk-restore", { ids: selectedIds });
      setSuccess(`${selectedIds.length} barang berhasil dipulihkan.`);
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memulihkan barang yang dipilih.");
    } finally { setBulkRestoring(false); }
  };

  /* ── Bulk Force Delete ── */
  const handleBulkForceDelete = async () => {
    setBulkDeleting(true);
    setBulkDeleteConfirm(false);
    try {
      await api.post("/items/bulk-force-delete", { ids: selectedIds });
      setSuccess(`${selectedIds.length} barang berhasil dihapus permanen.`);
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || "Gagal menghapus barang yang dipilih.");
    } finally { setBulkDeleting(false); }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Page Header ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items`)} size="small" sx={{
              border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
              color: C.text2, backgroundColor: C.surface,
              "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
              transition: "all 0.15s",
            }}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.amberBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ArchiveIcon sx={{ fontSize: 20, color: C.amber }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
                Arsip Barang
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                Barang yang telah diarsipkan — bisa dipulihkan kapan saja
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" size="small"
            startIcon={<InventoryIcon sx={{ fontSize: "15px !important" }} />}
            onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items`)}
            sx={{ borderColor: C.border, color: C.text2, textTransform: "none",
              borderRadius: "8px", fontSize: "0.8rem", fontWeight: 500, height: 34,
              "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg } }}>
            Daftar Barang Aktif
          </Button>
        </Box>
      </Box>

      {/* ── Alerts ── */}
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")}
          icon={<CheckCircleIcon fontSize="small" />}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.successBdr}` }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* ── Info banner ── */}
      <Box sx={{ mb: 2, p: "10px 14px", borderRadius: "10px",
        backgroundColor: C.amberBg, border: `1px solid ${C.amberBdr}`,
        display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon sx={{ fontSize: 16, color: C.amber, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.8rem", color: C.amber, fontWeight: 500 }}>
          Barang di bawah ini telah diarsipkan dan tidak muncul di daftar barang aktif.
          Klik <strong>Pulihkan</strong> untuk mengembalikannya.
        </Typography>
      </Box>

      {/* ── Search ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px",
        backgroundColor: C.surface, mb: 2, p: "12px 16px" }}>
        <TextField size="small" placeholder="Cari nama atau serial number..."
          value={searchInput} onChange={handleSearchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: C.text3 }} />
              </InputAdornment>
            ),
            endAdornment: loading && searchInput ? (
              <InputAdornment position="end">
                <CircularProgress size={13} sx={{ color: C.primary }} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ ...inputBase, width: { xs: "100%", sm: 320 } }}
        />
      </Paper>

      {/* ── Table Card ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>

        {/* Info bar */}
        <Box sx={{ px: 2, py: "10px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: `1px solid ${C.border}`,
          backgroundColor: selectedIds.length > 0 ? C.primaryBg : C.surfaceAlt,
          transition: "background 0.2s",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text3, fontWeight: 500 }}>
              {loading ? "Memuat data..." :
                totalItems > 0
                  ? `${(page - 1) * 10 + 1}–${Math.min(page * 10, totalItems)} dari ${totalItems} barang diarsipkan`
                  : "Tidak ada barang diarsipkan"}
            </Typography>
            {selectedIds.length > 0 && (
              <Fade in>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: C.primary }} />
                  <Typography sx={{ fontSize: "0.75rem", color: C.primary, fontWeight: 600 }}>
                    {selectedIds.length} item dipilih
                  </Typography>
                  <Button size="small" onClick={() => setSelectedIds([])}
                    sx={{ fontSize: "0.7rem", color: C.text3, textTransform: "none",
                      p: "0 4px", minWidth: 0, "&:hover": { color: C.danger } }}>
                    Batalkan
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading && (
              <LinearProgress sx={{ width: 80, height: 3, borderRadius: 4,
                backgroundColor: C.border,
                "& .MuiLinearProgress-bar": { backgroundColor: C.primary } }} />
            )}
            {!loading && items.length > 0 && (
              <Tooltip title={allPageSelected ? "Batal pilih halaman ini" : "Pilih semua di halaman ini"} arrow>
                <IconButton size="small" onClick={handleSelectAll}
                  sx={{ width: 28, height: 28, borderRadius: "6px",
                    color: allPageSelected ? C.primary : C.text3,
                    backgroundColor: allPageSelected ? C.primaryBg : "transparent",
                    border: `1px solid ${allPageSelected ? C.primary : C.border}`,
                    "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
                  }}>
                  {allPageSelected
                    ? <DeselectIcon sx={{ fontSize: 14 }} />
                    : <SelectAllIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                {/* Checkbox column */}
                <TableCell sx={{ ...thSx, width: 44, px: "8px" }}>
                  <Checkbox
                    size="small"
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    onChange={handleSelectAll}
                    disabled={loading || items.length === 0}
                    sx={{ p: 0.5, color: C.text3,
                      "&.Mui-checked": { color: C.primary },
                      "&.MuiCheckbox-indeterminate": { color: C.primary } }}
                  />
                </TableCell>
                {["No", "Serial Number", "Nama Barang", "Qty", "Kondisi", "Status", "Ruangan Terakhir", "Diarsipkan", "Aksi"].map(h => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, border: 0 }}>
                    <CircularProgress size={28} thickness={4} sx={{ color: C.primary }} />
                    <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: C.text3 }}>Memuat data...</Typography>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: "12px",
                      backgroundColor: C.surfaceAlt, display: "flex",
                      alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <ArchiveIcon sx={{ fontSize: 24, color: C.text3 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {search ? `Tidak ditemukan hasil untuk "${search}"` : "Tidak ada barang yang diarsipkan"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {search ? "Coba kata kunci lain" : "Semua barang masih aktif"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.map((item, idx) => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <TableRow key={item.id} sx={{
                    "&:hover": { backgroundColor: isSelected ? "#EFF6FF" : C.surfaceAlt },
                    backgroundColor: isSelected ? "#F0F7FF" : "transparent",
                    borderBottom: `1px solid ${C.border}`,
                    transition: "background 0.12s",
                    opacity: 0.85,
                  }}>

                    {/* Checkbox */}
                    <TableCell sx={{ ...tdSx, px: "8px", width: 44 }}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => handleSelectOne(item.id)}
                        sx={{ p: 0.5, color: C.text3,
                          "&.Mui-checked": { color: C.primary } }}
                      />
                    </TableCell>

                    {/* No */}
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.72rem", color: C.text3,
                        fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {(page - 1) * 10 + idx + 1}
                      </Typography>
                    </TableCell>

                    {/* Serial Number */}
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.75rem", color: C.text2,
                        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                        fontWeight: 500, whiteSpace: "nowrap" }}>
                        {item.serial_number || "—"}
                      </Typography>
                    </TableCell>

                    {/* Nama Barang */}
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.825rem", fontWeight: 600,
                        color: C.text1, whiteSpace: "nowrap" }}>
                        {item.name}
                      </Typography>
                      {item.category && (
                        <Typography sx={{ fontSize: "0.7rem", color: C.text3, mt: 0.25 }}>
                          {item.category}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Qty */}
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center",
                        justifyContent: "center", minWidth: 28, height: 22, px: 1,
                        borderRadius: "6px", backgroundColor: C.surfaceAlt,
                        border: `1px solid ${C.border}` }}>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700,
                          color: C.text1, fontVariantNumeric: "tabular-nums" }}>
                          {item.quantity ?? 1}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Kondisi */}
                    <TableCell sx={tdSx}>
                      <StatusChip map={CONDITION_MAP} value={item.condition} />
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={tdSx}>
                      <StatusChip map={STATUS_MAP} value={item.status} />
                    </TableCell>

                    {/* Ruangan Terakhir */}
                    <TableCell sx={tdSx}>
                      {item.room ? (
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 500,
                            color: C.text1, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                            {item.room.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: C.text3, fontFamily: "monospace" }}>
                            {item.room.code}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ color: C.text3, fontSize: "0.78rem" }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Diarsipkan */}
                    <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                        {fmtDate(item.deleted_at)}
                      </Typography>
                    </TableCell>

                    {/* Aksi */}
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                        <Tooltip title="Pulihkan barang ini" arrow>
                          <Button size="small" variant="outlined"
                            startIcon={<RestoreIcon sx={{ fontSize: "13px !important" }} />}
                            onClick={() => { setRestoreId(item.id); setRestoreItem(item); }}
                            sx={{ borderRadius: "7px", textTransform: "none",
                              fontSize: "0.75rem", fontWeight: 600,
                              borderColor: C.successBdr, color: C.success,
                              px: 1.5, height: 28,
                              "&:hover": { backgroundColor: C.successBg, borderColor: C.success } }}>
                            Pulihkan
                          </Button>
                        </Tooltip>
                        <Tooltip title="Hapus permanen" arrow>
                          <IconButton size="small"
                            onClick={() => { setDeleteId(item.id); setDeleteItem(item); }}
                            sx={{ width: 28, height: 28, borderRadius: "7px",
                              border: `1px solid ${C.dangerBdr}`, color: C.danger,
                              "&:hover": { backgroundColor: C.dangerBg, borderColor: C.danger } }}>
                            <DeleteForeverIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            px: 2, py: "10px", borderTop: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.75rem", color: C.text3 }}>
              Halaman {page} dari {totalPages}
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
              size="small" color="primary"
              sx={{
                "& .MuiPaginationItem-root": { fontSize: "0.75rem", borderRadius: "6px", minWidth: 28, height: 28 },
                "& .Mui-selected": { backgroundColor: `${C.primary} !important`, color: "#fff", fontWeight: 600 },
              }} />
          </Box>
        )}
      </Paper>

      {/* ── Single Restore Dialog ── */}
      <Dialog open={Boolean(restoreId)}
        onClose={() => { setRestoreId(null); setRestoreItem(null); }}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RestoreIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Pulihkan Barang?
              </Typography>
              {restoreItem && (
                <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                  <strong>{restoreItem.name}</strong> ({restoreItem.serial_number}) akan dikembalikan ke daftar barang aktif.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px",
            backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text2,
              display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: C.success }} />
              Barang akan muncul kembali di Daftar Barang setelah dipulihkan.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => { setRestoreId(null); setRestoreItem(null); }}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleRestore} disabled={restoring} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.success, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" } }}>
            {restoring ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Pulihkan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Single Force Delete Dialog ── */}
      <Dialog open={Boolean(deleteId)}
        onClose={() => { setDeleteId(null); setDeleteItem(null); }}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteForeverIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Hapus Permanen?
              </Typography>
              {deleteItem && (
                <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                  <strong>{deleteItem.name}</strong> ({deleteItem.serial_number}) akan dihapus selamanya.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px",
            backgroundColor: C.dangerBg, border: `1px solid ${C.dangerBdr}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.danger,
              display: "flex", alignItems: "center", gap: 0.75, fontWeight: 500 }}>
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              Tindakan ini tidak bisa dibatalkan. Data barang akan hilang selamanya.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => { setDeleteId(null); setDeleteItem(null); }}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleForceDelete} disabled={deleting} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {deleting ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Hapus Permanen"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Restore Confirm Dialog ── */}
      <Dialog open={bulkRestoreConfirm} onClose={() => setBulkRestoreConfirm(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RestoreIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Pulihkan {selectedIds.length} Barang?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua barang yang dipilih akan dikembalikan ke daftar barang aktif.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px",
            backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.text2,
              display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: C.success }} />
              Barang akan muncul kembali di Daftar Barang setelah dipulihkan.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setBulkRestoreConfirm(false)}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleBulkRestore} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.success, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" } }}>
            Pulihkan Semua
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Force Delete Confirm Dialog ── */}
      <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <DeleteForeverIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Hapus Permanen {selectedIds.length} Barang?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua barang yang dipilih akan dihapus selamanya.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: "10px 12px", borderRadius: "8px",
            backgroundColor: C.dangerBg, border: `1px solid ${C.dangerBdr}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: C.danger,
              display: "flex", alignItems: "center", gap: 0.75, fontWeight: 500 }}>
              <WarningAmberIcon sx={{ fontSize: 14 }} />
              Tindakan ini tidak bisa dibatalkan. Data barang akan hilang selamanya.
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setBulkDeleteConfirm(false)}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleBulkForceDelete} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2,
              backgroundColor: C.danger, fontWeight: 600, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            Hapus Permanen Semua
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Floating Bulk Action Bar ── */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        onBulkRestore={() => setBulkRestoreConfirm(true)}
        onBulkDelete={() => setBulkDeleteConfirm(true)}
        bulkRestoring={bulkRestoring}
        bulkDeleting={bulkDeleting}
      />
    </Box>
  );
}
